import React, { useEffect, useMemo, useRef, useState } from "react";
import { G, UI, useStore, useIsGM, openWindow, currentPageId, useCurrentPageId, controlsObj, type Tool } from "./store";
import { connect, disconnect, create, update, setGame, onFx, onToast, onChat, send } from "./net";
import { Board, LAYER_LABEL, resetExplored } from "./board/Board";
import { ChatPanel } from "./panels/Chat";
import { JournalPanel } from "./panels/Journal";
import { ArtPanel } from "./panels/Art";
import { JukeboxPanel, AudioEngine } from "./panels/Jukebox";
import { CollectionPanel, Hand } from "./panels/Collection";
import { SettingsPanel } from "./panels/Settings";
import { TurnTracker } from "./panels/TurnTracker";
import { Windows } from "./panels/Windows";
import { V, joinVoice, leaveVoice, toggleMute } from "./rtc";
import { sendChat } from "./chatSend";
import type { Layer, Page } from "../shared/types";
import { prompt } from "./util";

export function Game({ id }: { id: string }) {
  const connected = useStore(G, s => s.connected);
  const game = useStore(G, s => s.game);
  const error = useStore(G, s => s.error);
  const sideOpen = useStore(UI, s => s.sideOpen);

  useEffect(() => {
    connect(id);
    return () => { leaveVoice(); disconnect(); };
  }, [id]);

  // handouts pushed to players, explored resets
  useEffect(() => onFx(fx => {
    if (fx.kind === "show" && G.get().recs.handout[fx.handout]) openWindow("handout", fx.handout);
    if (fx.kind === "resetExplored") resetExplored(fx.pageId);
  }), []);

  // unread badge on the chat tab
  const [unread, setUnread] = useState(0);
  const tab = useStore(UI, s => s.tab);
  useEffect(() => onChat(() => { if (UI.get().tab !== "chat" || !UI.get().sideOpen) setUnread(u => u + 1); }), []);
  useEffect(() => { if (tab === "chat" && sideOpen) setUnread(0); }, [tab, sideOpen]);

  useEffect(() => { if (game) document.title = `${game.name} · Hearth`; return () => { document.title = "Hearth"; }; }, [game?.name]);

  if (error) return <div className="center-screen"><div className="card"><p>{error}</p><a href="#/">Back to your games</a></div></div>;
  if (!game) return <div className="center-screen"><div className="spinner" /> Connecting…</div>;

  return (
    <div className={`game${sideOpen ? "" : " side-closed"}`}>
      <div className="stage">
        <PageBar />
        <div className="stage-main">
          <Toolbar />
          <Board />
          {!connected && <div className="reconnecting">Reconnecting…</div>}
        </div>
        <BottomBar />
      </div>
      <Sidebar unread={unread} />
      <Windows />
      <TurnTracker />
      <Hand />
      <AudioEngine />
      <Toasts />
    </div>
  );
}

function Sidebar({ unread }: { unread: number }) {
  const tab = useStore(UI, s => s.tab);
  const open = useStore(UI, s => s.sideOpen);
  const gm = useIsGM();
  const tabs: [typeof tab, string, string][] = [
    ["chat", "💬", "Chat"], ["journal", "📖", "Journal"], ["art", "🖼", "Art library"], ["jukebox", "🎵", "Jukebox"], ["collection", "🧰", "Macros, tables & decks"], ["settings", "⚙", "Settings & help"],
  ];
  return (
    <aside className={`side${open ? "" : " closed"}`}>
      <div className="side-tabs">
        <button className="side-toggle" title={open ? "Hide sidebar" : "Show sidebar"} onClick={() => UI.set({ sideOpen: !open })}>{open ? "»" : "«"}</button>
        {tabs.filter(([t]) => t !== "art" || gm || true).map(([t, icon, label]) => (
          <button key={t} className={tab === t && open ? "on" : ""} title={label} onClick={() => UI.set({ tab: t, sideOpen: true })}>
            {icon}{t === "chat" && unread > 0 && <span className="badge">{unread > 99 ? "99+" : unread}</span>}
          </button>
        ))}
      </div>
      {open && (
        <div className="side-body">
          <div style={{ display: tab === "chat" ? "contents" : "none" }}><ChatPanel /></div>
          {tab === "journal" && <JournalPanel />}
          {tab === "art" && <ArtPanel />}
          {tab === "jukebox" && <JukeboxPanel />}
          {tab === "collection" && <CollectionPanel />}
          {tab === "settings" && <SettingsPanel />}
        </div>
      )}
    </aside>
  );
}

// ---------------- toolbar ----------------
const COLORS = ["transparent", "#000000", "#ffffff", "#d02020", "#e67e22", "#f1c40f", "#27ae60", "#2980b9", "#8e44ad", "#7f5539", "#95a5a6"];

function Toolbar() {
  const ui = useStore(UI, s => s);
  const gm = useIsGM();
  const pageId = useCurrentPageId();
  const page = useStore(G, s => s.recs.page[pageId]) as Page | undefined;
  const [fly, setFly] = useState<string | null>(null);
  const setTool = (t: Tool) => {
    UI.set({ tool: t, ...(t === "wall" || t === "door" ? { layer: "walls" as Layer } : {}) });
  };
  const btn = (t: Tool, icon: string, label: string, flyout?: string) => (
    <button
      className={ui.tool === t ? "on" : ""}
      title={label}
      onClick={() => { setTool(t); setFly(flyout && (fly !== flyout || ui.tool !== t) ? flyout : null); }}
    >{icon}</button>
  );
  return (
    <div className="toolbar" onPointerDown={e => e.stopPropagation()}>
      {btn("select", "➤", "Select & move (V)")}
      {btn("pan", "✋", "Pan (H)")}
      {btn("ruler", "📏", "Measure (R) — line, radius, cone, square", "ruler")}
      {btn("draw", "✏", "Draw (D)", "draw")}
      {btn("text", "𝐓", "Text (T)", "draw")}
      {gm && btn("fog", "🌫", "Fog of war — reveal / hide", "fog")}
      {gm && btn("wall", "🧱", "Walls for dynamic lighting (click points, double-click to finish)", "walls")}
      {gm && btn("door", "🚪", "Doors (drag a line)", "walls")}
      <div className="tb-sep" />
      {gm && (
        <button className="layer-btn" title="Active layer" onClick={() => setFly(fly === "layer" ? null : "layer")}>
          {{ map: "🗺", objects: "♟", gm: "👁", walls: "🧱" }[ui.layer]}
        </button>
      )}
      <button className={ui.turnOpen ? "on" : ""} title="Turn order" onClick={() => UI.set({ turnOpen: !ui.turnOpen })}>⏱</button>
      {gm && page?.lighting && (
        <button className={ui.gmDark > 0 ? "on" : ""} title="Preview players' darkness" onClick={() => UI.set({ gmDark: ui.gmDark > 0 ? 0 : 0.7 })}>🌑</button>
      )}

      {fly === "layer" && gm && (
        <div className="flyout" style={{ top: 0 }}>
          <div className="fly-title">Layer</div>
          {(["map", "objects", "gm", "walls"] as Layer[]).map(l => (
            <button key={l} className={ui.layer === l ? "on" : ""} onClick={() => { UI.set({ layer: l, selected: [] }); setFly(null); }}>{LAYER_LABEL[l]}</button>
          ))}
        </div>
      )}
      {fly === "ruler" && ui.tool === "ruler" && (
        <div className="flyout">
          <div className="fly-title">Measure</div>
          {(["line", "circle", "cone", "square"] as const).map(s => (
            <button key={s} className={ui.rulerShape === s ? "on" : ""} onClick={() => UI.set({ rulerShape: s })}>
              {{ line: "📏 Line", circle: "◯ Radius", cone: "◸ Cone", square: "▢ Square" }[s]}
            </button>
          ))}
        </div>
      )}
      {fly === "draw" && (ui.tool === "draw" || ui.tool === "text") && (
        <div className="flyout">
          <div className="fly-title">{ui.tool === "text" ? "Text" : "Draw"}</div>
          {ui.tool === "draw" && (
            <div className="fly-row">
              {(["free", "line", "rect", "ellipse", "poly"] as const).map(s => (
                <button key={s} className={ui.drawShape === s ? "on" : ""} title={s} onClick={() => UI.set({ drawShape: s })}>
                  {{ free: "〰", line: "╱", rect: "▭", ellipse: "◯", poly: "⬠" }[s]}
                </button>
              ))}
            </div>
          )}
          <div className="fly-label">{ui.tool === "text" ? "Color" : "Stroke"}</div>
          <Swatches value={ui.stroke} onChange={c => UI.set({ stroke: c })} />
          {ui.tool === "draw" && (
            <>
              <div className="fly-label">Fill</div>
              <Swatches value={ui.fill} onChange={c => UI.set({ fill: c })} />
              <div className="fly-label">Width {ui.strokeWidth}px</div>
              <input type="range" min={1} max={30} value={ui.strokeWidth} onChange={e => UI.set({ strokeWidth: +e.target.value })} />
            </>
          )}
          {ui.tool === "text" && (
            <>
              <div className="fly-label">Size {ui.fontSize}px</div>
              <input type="range" min={10} max={120} value={ui.fontSize} onChange={e => UI.set({ fontSize: +e.target.value })} />
            </>
          )}
        </div>
      )}
      {fly === "fog" && ui.tool === "fog" && page && (
        <div className="flyout">
          <div className="fly-title">Fog of war</div>
          <div className="fly-row">
            <button className={ui.fogMode === "reveal" ? "on" : ""} onClick={() => UI.set({ fogMode: "reveal" })}>☀ Reveal</button>
            <button className={ui.fogMode === "hide" ? "on" : ""} onClick={() => UI.set({ fogMode: "hide" })}>☁ Hide</button>
          </div>
          <div className="fly-row">
            <button className={ui.fogShape === "rect" ? "on" : ""} onClick={() => UI.set({ fogShape: "rect" })}>▭ Box</button>
            <button className={ui.fogShape === "poly" ? "on" : ""} onClick={() => UI.set({ fogShape: "poly" })}>⬠ Polygon</button>
          </div>
          <label className="chk"><input type="checkbox" checked={page.fogEnabled} onChange={e => update("page", page.id, { fogEnabled: e.target.checked })} /> Fog enabled on this page</label>
          <button onClick={() => update("page", page.id, { fog: [] })}>Hide everything</button>
          <button onClick={() => update("page", page.id, { fog: [{ mode: "reveal", pts: [0, 0, page.width * page.grid, 0, page.width * page.grid, page.height * page.grid, 0, page.height * page.grid] }] })}>Reveal everything</button>
          {page.fog?.length > 0 && <button onClick={() => update("page", page.id, { fog: page.fog.slice(0, -1) })}>Undo last fog edit</button>}
        </div>
      )}
      {fly === "walls" && (ui.tool === "wall" || ui.tool === "door") && page && (
        <div className="flyout">
          <div className="fly-title">Dynamic lighting</div>
          <label className="chk"><input type="checkbox" checked={page.lighting} onChange={e => update("page", page.id, { lighting: e.target.checked })} /> Lighting enabled</label>
          <label className="chk"><input type="checkbox" checked={page.globalIllum} onChange={e => update("page", page.id, { globalIllum: e.target.checked })} /> Daylight</label>
          <label className="chk"><input type="checkbox" checked={page.explorer} onChange={e => update("page", page.id, { explorer: e.target.checked })} /> Explorer mode</label>
          <div className="dim small">🧱 click to add wall points; double-click / Enter to finish. 🚪 drag to place a door (click its handle to open). Walls snap to half-squares; hold Alt for free placement. Select walls on the 🧱 layer to move or edit points.</div>
        </div>
      )}
    </div>
  );
}

function Swatches({ value, onChange }: { value: string; onChange: (c: string) => void }) {
  return (
    <div className="swatches">
      {COLORS.map(c => (
        <button key={c} className={`sw${value === c ? " on" : ""}${c === "transparent" ? " none" : ""}`} style={{ background: c === "transparent" ? undefined : c }} title={c === "transparent" ? "none" : c} onClick={() => onChange(c)} />
      ))}
      <input type="color" value={value === "transparent" ? "#000000" : value} onChange={e => onChange(e.target.value)} />
    </div>
  );
}

// ---------------- pages ----------------
function PageBar() {
  const gm = useIsGM();
  const pages = useStore(G, s => s.recs.page);
  const game = useStore(G, s => s.game)!;
  const view = useCurrentPageId();
  const [open, setOpen] = useState(true);
  if (!gm) return null;
  const list = Object.values(pages).sort((a, b) => a.order - b.order);
  const splits = Object.entries(game.state.playerPages);
  return (
    <div className={`pagebar${open ? "" : " closed"}`}>
      <button className="pb-toggle" onClick={() => setOpen(!open)} title="Pages">{open ? "▴" : "▾ Pages"}</button>
      {open && (
        <>
          {list.map((p, i) => {
            const players = game.state.playerPage === p.id;
            const split = splits.filter(([, pid]) => pid === p.id).length;
            return (
              <div key={p.id} className={`page-tab${view === p.id ? " on" : ""}`} onClick={() => UI.set({ viewPage: p.id })}
                draggable onDragStart={e => e.dataTransfer.setData("application/x-hearth-page", p.id)}
                onDragOver={e => e.preventDefault()}
                onDrop={e => {
                  const from = e.dataTransfer.getData("application/x-hearth-page");
                  if (!from || from === p.id) return;
                  const order = list.filter(x => x.id !== from);
                  order.splice(i, 0, pages[from]);
                  order.forEach((x, k) => x.order !== k && update("page", x.id, { order: k }));
                }}>
                <span className="pt-name">{p.name}</span>
                <button className={`flag${players ? " on" : ""}`} title={players ? "Players are here" : "Move players to this page"} onClick={e => { e.stopPropagation(); setGame({ playerPage: p.id, playerPages: {} }); }}>▶</button>
                {split > 0 && <span className="pill" title="Players split onto this page">{split}</span>}
                <button className="mini" title="Page settings" onClick={e => { e.stopPropagation(); openWindow("page", p.id); }}>⚙</button>
              </div>
            );
          })}
          <button className="page-add" title="Add page" onClick={async () => {
            const name = await prompt("Page name", `Page ${list.length + 1}`);
            if (!name) return;
            const id = await create("page", { name });
            UI.set({ viewPage: id });
          }}>＋</button>
        </>
      )}
    </div>
  );
}

// ---------------- bottom: macro bar + players ----------------
function BottomBar() {
  const macros = useStore(G, s => s.recs.macro);
  const me = useStore(G, s => s.me);
  const sel = useStore(UI, s => s.selected);
  const objs = useStore(G, s => s.recs.obj);
  const chars = useStore(G, s => s.recs.char);
  const bar = Object.values(macros).filter(m => m.inBar && (m.owner === me));
  const tok = sel.length === 1 ? objs[sel[0]] : null;
  const ch = tok?.represents ? chars[tok.represents] : null;
  const tokenActions = tok && controlsObj(tok) ? [
    ...(ch?.abilities || []).filter(a => a.tokenAction).map(a => ({ id: a.id, name: a.name, run: () => sendChat(a.action, ch!.id) })),
    ...Object.values(macros).filter(m => m.tokenAction && (m.owner === me || m.shared)).map(m => ({ id: m.id, name: m.name, run: () => sendChat(m.action) })),
  ] : [];
  return (
    <div className="bottombar">
      <div className="macrobar">
        {tokenActions.map(a => <button key={a.id} className="tokact" onClick={a.run}>{a.name}</button>)}
        {bar.map(m => <button key={m.id} onClick={() => sendChat(m.action)}>{m.name}</button>)}
      </div>
      <Players />
    </div>
  );
}

function Players() {
  const members = useStore(G, s => s.members);
  const online = useStore(G, s => s.online);
  const me = useStore(G, s => s.me);
  const v = useStore(V, s => s);
  const on = new Map(online.map(p => [p.uid, p]));
  const sorted = [...members].sort((a, b) => (on.has(b.uid) ? 1 : 0) - (on.has(a.uid) ? 1 : 0));
  return (
    <div className="players">
      {sorted.map(m => {
        const p = on.get(m.uid);
        const stream = m.uid === me ? v.local : v.streams[m.uid];
        return (
          <div key={m.uid} className={`player${p ? " online" : ""}`} title={`${m.display}${m.role === "gm" ? " (GM)" : ""}${p ? "" : " — offline"}`}>
            {stream && (p?.video || (m.uid === me && v.video)) ? (
              <Video stream={stream} muted={m.uid === me} />
            ) : (
              <div className="pav" style={{ background: m.color }}>{m.display.slice(0, 1).toUpperCase()}</div>
            )}
            {stream && m.uid !== me && !p?.video && <Audio stream={stream} />}
            <div className="pname">{m.role === "gm" ? "👑 " : ""}{m.display}{p?.voice ? " 🎙" : ""}</div>
          </div>
        );
      })}
      <div className="voice-ctl">
        {!v.joined ? (
          <>
            <button onClick={() => joinVoice(false)} title="Join voice chat">🎙 Voice</button>
            <button onClick={() => joinVoice(true)} title="Join with camera">📹 Video</button>
          </>
        ) : (
          <>
            <button onClick={toggleMute} className={v.muted ? "danger" : ""}>{v.muted ? "🔇 Muted" : "🎙 Mute"}</button>
            <button onClick={leaveVoice}>Leave</button>
          </>
        )}
      </div>
    </div>
  );
}

function Video({ stream, muted }: { stream: MediaStream; muted: boolean }) {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => { if (ref.current && ref.current.srcObject !== stream) ref.current.srcObject = stream; }, [stream]);
  return <video ref={ref} autoPlay playsInline muted={muted} className="pvid" />;
}
function Audio({ stream }: { stream: MediaStream }) {
  const ref = useRef<HTMLAudioElement>(null);
  useEffect(() => { if (ref.current && ref.current.srcObject !== stream) ref.current.srcObject = stream; }, [stream]);
  return <audio ref={ref} autoPlay />;
}

function Toasts() {
  const [list, setList] = useState<{ id: number; msg: string }[]>([]);
  useEffect(() => onToast(msg => {
    const id = Math.random();
    setList(l => [...l.slice(-3), { id, msg }]);
    setTimeout(() => setList(l => l.filter(t => t.id !== id)), 3500);
  }), []);
  return <div className="toasts">{list.map(t => <div key={t.id} className="toast">{t.msg}</div>)}</div>;
}

export { send };
